<template>
  <div v-if="mostrar" class="modal-overlay" @click.self="fechar">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Importar Pacientes</h2>
        <button class="btn-fechar" @click="fechar">&times;</button>
      </div>

      <div class="modal-body">
        <div class="instrucoes">
          <p><strong>Formato aceito:</strong> CSV (separado por ; ou ,)</p>
          <p><strong>Colunas esperadas:</strong></p>
          <code>Primeiro Nome; Sobrenome; Data Nascimento; Idade; Sexo; Indicação Clínica; Forma de Captação; Sistema; UF; Email; Telefone; Receber Email; Receber WhatsApp</code>
          <p class="instrucoes-nota"><strong>Nota:</strong> "Receber Email" e "Receber WhatsApp" aceitam Sim/Não. Se não informados, o padrão é <strong>Sim</strong>.</p>
        </div>

        <div
          class="upload-area"
          :class="{ 'dragover': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.txt"
            class="file-input"
            @change="handleFileSelect"
          />
          <div class="upload-content">
            <span class="upload-icon">📁</span>
            <p v-if="!arquivo">Arraste um arquivo CSV aqui ou clique para selecionar</p>
            <p v-else class="arquivo-nome">{{ arquivo.name }}</p>
          </div>
        </div>

        <div v-if="resultado" class="resultado" :class="resultado.erros.length > 0 ? 'com-erros' : 'sucesso'">
          <p><strong>{{ resultado.message }}</strong></p>
          <div v-if="resultado.erros.length > 0" class="erros-lista">
            <p>Erros encontrados:</p>
            <ul>
              <li v-for="(erro, index) in resultado.erros.slice(0, 10)" :key="index">{{ erro }}</li>
              <li v-if="resultado.erros.length > 10">... e mais {{ resultado.erros.length - 10 }} erros</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancelar" @click="fechar">Cancelar</button>
        <button
          class="btn-importar"
          :disabled="!arquivo || isLoading"
          @click="importar"
        >
          {{ isLoading ? 'Importando...' : 'Importar' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';

defineProps({
  mostrar: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['fechar', 'importado']);

const fileInput = ref(null);
const arquivo = ref(null);
const isDragging = ref(false);
const isLoading = ref(false);
const resultado = ref(null);

const handleFileSelect = (event) => {
  const files = event.target.files;
  if (files.length > 0) {
    arquivo.value = files[0];
    resultado.value = null;
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    arquivo.value = files[0];
    resultado.value = null;
  }
};

const importar = async () => {
  if (!arquivo.value) return;

  isLoading.value = true;
  resultado.value = null;

  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo.value);

    const response = await axios.post('/api/pacientes/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Accept': 'application/json'
      }
    });

    resultado.value = response.data;

    if (response.data.importados > 0) {
      emit('importado');
    }
  } catch (error) {
    resultado.value = {
      message: 'Erro ao importar: ' + (error.response?.data?.message || error.message),
      importados: 0,
      erros: []
    };
  } finally {
    isLoading.value = false;
  }
};

const fechar = () => {
  arquivo.value = null;
  resultado.value = null;
  emit('fechar');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.dark-mode .modal-container {
  background: #1e1e1e;
  color: #f1f1f1;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.dark-mode .modal-header {
  border-bottom-color: #333;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
}

.btn-fechar {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.dark-mode .btn-fechar {
  color: #aaa;
}

.modal-body {
  padding: 20px;
}

.instrucoes {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
}

.dark-mode .instrucoes {
  background: #2a2a2a;
}

.instrucoes code {
  display: block;
  margin-top: 8px;
  padding: 8px;
  background: #e8e8e8;
  border-radius: 4px;
  font-size: 11px;
  word-break: break-all;
}

.dark-mode .instrucoes code {
  background: #333;
}

.instrucoes-nota {
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.dark-mode .instrucoes-nota {
  color: #aaa;
}

.upload-area {
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.upload-area:hover,
.upload-area.dragover {
  border-color: #4caf50;
  background: #f0fff0;
}

.dark-mode .upload-area:hover,
.dark-mode .upload-area.dragover {
  background: #1a2e1a;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.upload-icon {
  font-size: 40px;
  display: block;
  margin-bottom: 10px;
}

.arquivo-nome {
  color: #4caf50;
  font-weight: 500;
}

.resultado {
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
}

.resultado.sucesso {
  background: #d4edda;
  color: #155724;
}

.dark-mode .resultado.sucesso {
  background: #1b4332;
  color: #95d5b2;
}

.resultado.com-erros {
  background: #fff3cd;
  color: #856404;
}

.dark-mode .resultado.com-erros {
  background: #3d3200;
  color: #ffc107;
}

.erros-lista {
  margin-top: 10px;
  font-size: 12px;
}

.erros-lista ul {
  margin: 5px 0;
  padding-left: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
}

.dark-mode .modal-footer {
  border-top-color: #333;
}

.btn-cancelar,
.btn-importar {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-cancelar {
  background: #e0e0e0;
  border: none;
  color: #333;
}

.btn-cancelar:hover {
  background: #d0d0d0;
}

.dark-mode .btn-cancelar {
  background: #333;
  color: #f1f1f1;
}

.btn-importar {
  background: #4caf50;
  border: none;
  color: white;
}

.btn-importar:hover:not(:disabled) {
  background: #388e3c;
}

.btn-importar:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* 📱 Responsivo Mobile */
@media (max-width: 600px) {
  .modal-container {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    padding: 12px 16px;
    flex-shrink: 0;
  }

  .modal-header h2 {
    font-size: 16px;
  }

  .btn-fechar {
    font-size: 28px;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-body {
    padding: 16px;
    flex: 1;
    overflow-y: auto;
  }

  .instrucoes {
    font-size: 12px;
    padding: 10px;
  }

  .instrucoes code {
    font-size: 10px;
  }

  .upload-area {
    padding: 24px 16px;
  }

  .upload-icon {
    font-size: 32px;
  }

  .modal-footer {
    padding: 12px 16px;
    flex-shrink: 0;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }

  .btn-cancelar,
  .btn-importar {
    padding: 12px 16px;
    font-size: 15px;
    min-height: 48px;
    flex: 1;
  }
}
</style>
